/**
 * `page` metric-label normaliser (round-5 K4).
 *
 * Reduces a raw `location.pathname` to the closed `page` vocabulary — the path's
 * FIRST segment (`/estates/{slug}` → `/estates`, `/listings/buy` → `/listings`),
 * or `other` when that section is not declared. This is the fix for the
 * unbounded-cardinality bug: web-vitals used to label every metric with the raw
 * pathname, so each `/estates/{slug}` / `/districts/{slug}` / `/blog/{slug}`
 * visit minted a fresh `web_*{page=…}` Prometheus series once the BFF re-emitted
 * it as OTLP.
 *
 * The vocabulary is read from the GENERATED metric catalog (SSoT:
 * `web/manifests/metrics.yaml`), never re-listed here — and because the report
 * helpers take the generated `Web*Page` union, a raw pathname is now a `tsc`
 * error at the call site, not a silent cardinality explosion.
 */
import { WEB_METRIC_CATALOG, type WebLcpMsPage } from '@/shared/lib/metrics/client-metrics_gen';

/** The closed section vocabulary, from the generated catalog (all vitals metrics
 * share the same `page` vocab, so `web_lcp_ms` is a representative source). */
const PAGE_SECTIONS: ReadonlySet<string> = new Set(
  WEB_METRIC_CATALOG.web_lcp_ms.labelValues['page'] ?? []
);

/**
 * Normalise `pathname` to its closed `page` section label. The return type is the
 * generated union, so callers pass it straight to the typed `report*` helpers.
 */
export function pageLabel(pathname: string): WebLcpMsPage {
  const first = pathname.split('/')[1] ?? '';
  const section = first === '' ? '/' : `/${first}`;
  // `section` is either a declared vocab value or folded to the declared `other`.
  return (PAGE_SECTIONS.has(section) ? section : 'other') as WebLcpMsPage;
}
